import React from 'react';
import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { statusToneTokens, type StatusTone } from '../../constants/statusTokens';

export interface StatusChipProps {
  tone: StatusTone;
  label: React.ReactNode;
  /** Pulse the dot — only for something in progress right now. */
  pulse?: boolean;
  size?: 'sm' | 'md';
  /** Text only with a dot, no tinted background (dense tables). */
  bare?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Operational status: a dot plus a text label (colour is never the only
 * signal). Unassigned renders as an outlined neutral chip.
 */
const StatusChip: React.FC<StatusChipProps> = ({ tone, label, pulse = false, size = 'sm', bare = false, sx }) => {
  const theme = useTheme();
  const t = statusToneTokens(theme.palette.mode, tone);
  const outlined = tone === 'unassigned';
  return (
    <Box
      component="span"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          maxWidth: '100%',
          height: size === 'sm' ? 22 : 26,
          px: bare ? 0 : 1,
          borderRadius: `${CONTENT_SHAPE.xs}px`,
          bgcolor: bare ? 'transparent' : t.soft,
          border: bare ? 0 : `1px ${outlined ? 'dashed' : 'solid'} ${t.border}`,
          color: t.text,
          fontSize: size === 'sm' ? '0.75rem' : '0.8125rem',
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: outlined ? 'transparent' : t.fill,
          border: outlined ? `1.5px solid ${t.border}` : 0,
          ...(pulse
            ? {
                '--zero-pulse-color': t.soft,
                animation: 'zero-live-pulse 2.2s ease-out infinite',
              }
            : {}),
        }}
      />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Box>
    </Box>
  );
};

export default StatusChip;
